import { Controller } from "@hotwired/stimulus"
import { focusSearchForKey, buildHighlightRegex, splitTextForHighlight, noteDrawerIsStale } from "../lib/activity_feed_search.mjs"

const HIGHLIGHT_CLASS = "activity-search-highlight"
// Tags whose text we never rewrap (form controls, code we shouldn't split, existing marks).
const SKIP_TAGS = new Set(["MARK", "SCRIPT", "STYLE", "INPUT", "TEXTAREA", "SELECT", "OPTION", "KBD"])
// Wait for a lull in typing before filtering so we don't fire a request per keystroke.
const DEBOUNCE_MS = 250

// Drives the claim activity filter: focuses the search box on "/", filters the feed in real time
// as the user types (debounced turbo-frame submit — no explicit submit button), clears on Escape,
// and highlights the active query in the rendered timeline (including lazily loaded pages).
export default class extends Controller {
  static targets = ["input", "clear", "results", "addNote"]

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.onFrameLoad = this.onFrameLoad.bind(this)
    this.onSubmitEnd = this.onSubmitEnd.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
    // Older/refreshed feed pages arrive via Turbo frames inside this element; re-highlight them
    // and keep the add-note drawer URL pointed at the current search.
    this.element.addEventListener("turbo:frame-load", this.onFrameLoad)
    // Drop a deferred add-note open if its search genuinely fails (see onSubmitEnd).
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)
    this.openDrawerAfterResults = false
    this.toggleClear()
    this.applyHighlights()
    this.syncNoteDrawer()
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    this.element.removeEventListener("turbo:frame-load", this.onFrameLoad)
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    this.cancelPending()
  }

  onFrameLoad(event) {
    this.applyHighlights()
    this.syncNoteDrawer()
    this.releaseDeferredDrawer(event)
  }

  // A deferred add-note open (see onAddNote) may only fire once the results frame has rendered the
  // query the user currently has typed. Turbo aborts the prior in-flight submission whenever a newer
  // one starts (FrameController#formSubmitted -> formSubmission.stop()), so the frame can't be left
  // showing an older query's results — but a slow earlier response can still land first, so we gate
  // the open on the loaded canonical query matching the current input rather than on any load. Lazy
  // pagination frames (claim-feed-after-*) don't reflect a search, so they never release it.
  releaseDeferredDrawer(event) {
    if (!this.openDrawerAfterResults) return

    const frame = event && event.target
    const isResultsFrame = frame && typeof frame.id === "string" &&
      frame.id.startsWith("claim-activity-feed-results-")
    if (!isResultsFrame) return

    if (!this.hasAddNoteTarget) return

    const loaded = this.hasResultsTarget ? (this.resultsTarget.dataset.activitySearchQuery || "") : ""
    if (noteDrawerIsStale(this.inputTarget.value, loaded)) return

    this.openDrawerAfterResults = false
    // Re-fire the real click now that the drawer URL is synced: onAddNote sees a fresh query and
    // falls through, and page#openDrawer runs as it would for a direct user click — so we never
    // reimplement (and risk diverging from) its open contract.
    this.addNoteTarget.click()
  }

  // The add-note button lives outside the reloaded results frame, so its drawer URL only picks up
  // the current token on frame load. If the user opens it during the debounce or while a filter is
  // in flight, the URL still holds the previous token — so defer the open, flush the pending
  // search, and let releaseDeferredDrawer reopen it once results for the current query have landed.
  onAddNote(event) {
    if (!this.hasInputTarget) return

    const loaded = this.hasResultsTarget ? (this.resultsTarget.dataset.activitySearchQuery || "") : ""
    if (!noteDrawerIsStale(this.inputTarget.value, loaded)) return

    event.preventDefault()
    event.stopImmediatePropagation()
    this.openDrawerAfterResults = true
    this.submit()
  }

  // Turbo fires submit-end for every finished search: success:true on a rendered response,
  // success:false on a genuine network/HTTP error, and success:undefined when a newer submit
  // aborted this one. Only a genuine failure should abandon a deferred add-note open — an abort
  // means a newer request is on its way and will release it, so leave the flag set for that case.
  onSubmitEnd(event) {
    if (event.detail && event.detail.success === false) {
      this.openDrawerAfterResults = false
    }
  }

  // Live filtering never re-renders the add-note button, so its drawer URL would keep the token it
  // had at page load and a note saved mid-filter would drop the search. Re-point it at the token
  // the server just rendered onto the results frame after each filter.
  syncNoteDrawer() {
    if (!this.hasAddNoteTarget) return

    const token = this.hasResultsTarget ? (this.resultsTarget.dataset.activitySearchToken || "") : ""
    const url = new URL(this.addNoteTarget.dataset.drawerUrl, window.location.origin)
    if (token) {
      url.searchParams.set("activity_search_token", token)
    } else {
      url.searchParams.delete("activity_search_token")
    }
    this.addNoteTarget.dataset.drawerUrl = `${url.pathname}${url.search}`
  }

  handleKeydown(event) {
    focusSearchForKey(event, this.inputTarget)
  }

  // Debounced live filter: submit the search form (targets the feed frame) after a typing lull.
  onInput() {
    this.toggleClear()
    this.cancelPending()
    this.debounceTimer = setTimeout(() => this.submit(), DEBOUNCE_MS)
  }

  // Enter submits immediately; drop the pending debounce so we don't fire a duplicate request.
  onSubmit() {
    this.cancelPending()
  }

  // Escape clears the filter and drops focus, matching the native "cancel search" gesture.
  onKeydown(event) {
    if (event.key !== "Escape") return

    event.preventDefault()
    this.clear()
    this.inputTarget.blur()
  }

  clear() {
    // Reset to the unfiltered feed whenever the input has text OR the displayed results are still
    // filtered/expired — an expired view renders an empty input, so an input-only check would leave
    // Escape stranded on the expired state with nothing to clear.
    const needsReset = this.inputTarget.value !== "" || this.resultsFiltered()
    this.inputTarget.value = ""
    this.toggleClear()
    if (needsReset) this.submit()
  }

  resultsFiltered() {
    return this.hasResultsTarget && this.resultsTarget.dataset.activitySearchActive === "true"
  }

  submit() {
    this.cancelPending()
    const form = this.inputTarget.form
    if (form) form.requestSubmit()
  }

  cancelPending() {
    if (!this.debounceTimer) return

    clearTimeout(this.debounceTimer)
    this.debounceTimer = null
  }

  // Toggle via classes (not the `hidden` property): the button also carries `flex`, whose
  // display would win over the [hidden] attribute and keep it visible when the field is blank.
  toggleClear() {
    if (!this.hasClearTarget) return

    const blank = this.inputTarget.value.trim() === ""
    this.clearTarget.classList.toggle("hidden", blank)
    this.clearTarget.classList.toggle("flex", !blank)
  }

  itemsContainer() {
    return this.element.querySelector('[id^="claim-activity-feed-items-"]')
  }

  // Highlight the query the server actually searched (normalized + truncated), which the results
  // frame carries — not the raw mounted input, which may hold whitespace or overlength text that
  // was never matched. The input fallback is only a guard for a missing results node.
  highlightQuery() {
    if (this.hasResultsTarget && this.resultsTarget.dataset.activitySearchQuery !== undefined) {
      return this.resultsTarget.dataset.activitySearchQuery
    }
    return this.hasInputTarget ? this.inputTarget.value : ""
  }

  applyHighlights() {
    const container = this.itemsContainer()
    if (!container) return

    this.clearHighlights(container)

    const query = this.highlightQuery()
    const regex = buildHighlightRegex(query)
    if (!regex) return

    this.highlightTextNodes(container, query)
  }

  clearHighlights(container) {
    container.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`).forEach((mark) => {
      const parent = mark.parentNode
      if (!parent) return
      while (mark.firstChild) parent.insertBefore(mark.firstChild, mark)
      parent.removeChild(mark)
      parent.normalize()
    })
  }

  highlightTextNodes(container, query) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT
        const parentTag = node.parentElement && node.parentElement.tagName
        if (parentTag && SKIP_TAGS.has(parentTag)) return NodeFilter.FILTER_REJECT
        return NodeFilter.FILTER_ACCEPT
      },
    })

    const textNodes = []
    let current
    while ((current = walker.nextNode())) textNodes.push(current)

    textNodes.forEach((node) => {
      const segments = splitTextForHighlight(node.nodeValue, query)
      if (!segments.some((segment) => segment.match)) return

      const fragment = document.createDocumentFragment()
      segments.forEach((segment) => {
        if (segment.match) {
          const mark = document.createElement("mark")
          mark.className = HIGHLIGHT_CLASS
          mark.textContent = segment.text
          fragment.appendChild(mark)
        } else {
          fragment.appendChild(document.createTextNode(segment.text))
        }
      })

      node.parentNode.replaceChild(fragment, node)
    })
  }
}
